// @need-translate
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { NextPageContext } from 'next/types';
import { ReactElement } from 'react';
import { redirectInInitialProps } from '../../../common/redirectInInitialProps';
import { requestContext } from '../../../common/requestContext';
import { isTrue } from '../../../common/shared/utils/isTrue';
import { isValidEnumValue } from '../../../common/shared/utils/isValidEnumValue';
import { PolicyType } from '../../../common/shared/utils/PolicyType.enum';
import { twMerge } from '../../../common/twMerge';
import { Divider } from '../../../design-system/atoms/Divider/Divider';
import { Icon } from '../../../design-system/atoms/Icon/Icon';
import { IconType } from '../../../design-system/atoms/Icon/IconType.enum';
import { Text } from '../../../design-system/atoms/Text/Text';
import { Layout } from '../../../design-system/layout/Layout';
import { MobileLayoutConfig } from '../../../design-system/layout/MobileLayoutConfig.interface';
import { MainTab } from '../../../design-system/organisms/MainTab/MainTab';
import { SelectTermsType } from '../../../design-system/pages/policy/SelectTermsType';
import { POLICY_TAB, POLICY_TITLE_MAP, PolicyPageStyle } from '../../../domain/policy/constant';
import { CommonMobileHeaderType, ExceptionMobileHeaderType } from '../../../header/constants';
import { theme } from '../../../theme';
const Terms = dynamic(() => import('../../../design-system/pages/policy/Terms/index').then(module => module.Terms));
const TermsPaid = dynamic(() => import('../../../design-system/pages/policy/TermsPaid/index').then(module => module.TermsPaid));
const Privacy = dynamic(() => import('../../../design-system/pages/policy/Privacy').then(module => module.Privacy));
const SafeGuard = dynamic(() => import('../../../design-system/pages/policy/SafeGuard').then(module => module.SafeGuard));
const Operation = dynamic(() => import('../../../design-system/pages/policy/Operation').then(module => module.Operation));
const Right = dynamic(() => import('../../../design-system/pages/policy/Right').then(policy => policy.Right));
const Marketing = dynamic(() => import('../../../design-system/pages/policy/Marketing').then(module => module.Marketing));
const Friends = dynamic(() => import('../../../design-system/pages/policy/Friends').then(module => module.Friends));
const PersonalInformation = dynamic(() => import('../../../design-system/pages/policy/PersonalInformation').then(module => module.PersonalInformation));
const MarketingNight = dynamic(() => import('../../../design-system/pages/policy/MarketingNight').then(module => module.MarketingNight));
const UsePersonalInfo = dynamic(() => import('../../../design-system/pages/policy/UsePersonalInfo').then(module => module.UsePersonalInfo));
const PersonalAd = dynamic(() => import('../../../design-system/pages/policy/PersonalAd').then(module => module.PersonalAd));
const PaymentService = dynamic(() => import('../../../design-system/pages/policy/PaymentService').then(module => module.PaymentService));
const PrivacySafe = dynamic(() => import('../../../design-system/pages/policy/PrivacySafe').then(module => module.PrivacySafe));
const MarketingThird = dynamic(() => import('../../../design-system/pages/policy/MarketingThird').then(module => module.MarketingThird));
const LegalAgentPrivacy = dynamic(() => import('../../../design-system/pages/policy/LegalAgentPrivacy').then(module => module.LegalAgentPrivacy));
const ChildPrivacy = dynamic(() => import('../../../design-system/pages/policy/ChildPrivacy').then(module => module.ChildPrivacy));
const ChildPersonalInformationThird = dynamic(() => import('../../../design-system/pages/policy/ChildPersonalInformationThird').then(module => module.ChildPersonalInformationThird));
const ChildMarketingThird = dynamic(() => import('../../../design-system/pages/policy/ChildMarketingThird').then(module => module.ChildMarketingThird));
const PersonalInformationCollection = dynamic(() => import('../../../design-system/pages/policy/PersonalInformationCollection').then(module => module.PersonalInformationCollection));
const PersonalInformationThirdParty = dynamic(() => import('../../../design-system/pages/policy/PersonalInformationThirdParty').then(module => module.PersonalInformationThirdParty));
interface PolicyProps {
  policyType: PolicyType;
  /** 웹뷰 환경의 약관 페이지에서 약관 상세보기로 이동한 후에 약관페이지로 다시 이동할 수 있는 버튼을 노출합니다.
   * 인증 페이지의 약관 상세보기로 이동하는 경우 사용됩니다.
   */
  needCloseButton?: boolean;
  hideMenu?: boolean;
  /**
   * 새창에서 띄울 경우 모바일 뷰로 보여주는데 사용합니다.
   */
  showNewWindow?: boolean;
}
function PolicyTypePage({
  policyType,
  needCloseButton,
  hideMenu
}: PolicyProps) {
  const router = useRouter();
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  const ContentsComponent = CONTENTS_COMPONENT_MAP[policyType];
  const currentTabItemId = policyType === PolicyType.TermsPaid ? PolicyType.Terms : policyType;
  return <div className="w-full text-theme-solid-100">
      {isWebView && needCloseButton && !hideMenu && <div className="relative flex h-56pxr items-center bg-theme-bg-surface-alt">
          <Text className="font-medium2-bold flex-1 text-center text-theme-solid-100">
            약관 상세
          </Text>
          <Icon className="absolute top-1/2 right-10pxr -translate-y-1/2" onClick={router.back} type={IconType.NavigationClose} />
        </div>}

      {!isWebView && !hideMenu && <>
          {POLICY_TAB.map(({
        id
      }) => id).includes(currentTabItemId) && <MainTab className={!isMobile ? 'mb-0' : undefined} tabItems={POLICY_TAB} currentTabItemId={currentTabItemId} isMobile={isMobile} />}
          {isMobile && <Divider color={theme.colors['theme-transparent-20']} />}
        </>}
      <div className={twMerge('px-15pxr pb-35pxr', !isMobile && 'mt-32pxr')}>
        {ContentsComponent &&
      // eslint-disable-next-line tailwindcss/no-custom-classname
      <div className={twMerge('policy-content-container', 'inline-block')}>
            {!hideMenu && !isWebView && SELECTABLE_TERMS_TYPE.includes(policyType) && <SelectTermsType termsType={policyType} />}
            <ContentsComponent isMobile={isMobile} />
          </div>}
      </div>
      <style jsx>{PolicyPageStyle}</style>
    </div>;
}
PolicyTypePage.getInitialProps = async ({
  query,
  res
}: NextPageContext): Promise<PolicyProps> => {
  const otherParams = query?.pathParams as string[];
  const type = otherParams.length > 0 ? otherParams[0] : undefined;
  if (type && !isValidEnumValue(PolicyType, type)) {
    redirectInInitialProps({
      destination: '/404',
      res
    });
    // @ts-ignore
    return {};
  }
  const policyType = type as PolicyType || PolicyType.Terms;
  return {
    policyType,
    needCloseButton: isTrue(query.needCloseButton as string | undefined),
    hideMenu: isTrue(query.hideMenu as string | undefined),
    showNewWindow: isTrue(query.showNewWindow as string | undefined)
  };
};
PolicyTypePage.getLayout = function getLayout(page: ReactElement) {
  const policyType = page.props.policyType as PolicyType;
  const hideMenu = page.props.hideMenu;
  const showNewWindow = page.props.showNewWindow;
  return <Layout title={POLICY_TITLE_MAP[policyType]} mobileLayoutConfig={{
    shouldShowHeader: !hideMenu,
    headerType: CommonMobileHeaderType.FourDepth,
    ...(showNewWindow && NEW_WINDOW_LAYOUT_CONFIG)
  }} pcLayoutConfig={{
    shouldUseMobileHeader: policyType === PolicyType.UsePersonalInfo || showNewWindow,
    shouldShowHeader: true,
    shouldShowFooter: false
  }}>
      {page}
    </Layout>;
};
const CONTENTS_COMPONENT_MAP = {
  [PolicyType.Terms]: Terms,
  [PolicyType.Privacy]: Privacy,
  [PolicyType.TermsPaid]: TermsPaid,
  [PolicyType.SafeGuard]: SafeGuard,
  [PolicyType.Operation]: Operation,
  [PolicyType.Right]: Right,
  [PolicyType.Marketing]: Marketing,
  [PolicyType.Friends]: Friends,
  [PolicyType.PersonalInformationCollection]: PersonalInformationCollection,
  [PolicyType.PersonalInformationThirdParty]: PersonalInformationThirdParty,
  [PolicyType.PersonalInformation]: PersonalInformation,
  [PolicyType.MarketingNight]: MarketingNight,
  [PolicyType.UsePersonalInfo]: UsePersonalInfo,
  [PolicyType.PersonalAd]: PersonalAd,
  [PolicyType.PaymentService]: PaymentService,
  [PolicyType.PrivacySafe]: PrivacySafe,
  [PolicyType.MarketingThird]: MarketingThird,
  // 14세 관련 약관
  [PolicyType.LegalAgentPrivacy]: LegalAgentPrivacy,
  [PolicyType.ChildPrivacy]: ChildPrivacy,
  [PolicyType.ChildPersonalInformationThird]: ChildPersonalInformationThird,
  [PolicyType.ChildMarketingThird]: ChildMarketingThird
};
const SELECTABLE_TERMS_TYPE = [PolicyType.Terms, PolicyType.TermsPaid];
export default PolicyTypePage;

/**
 * 가입 페이지, 인증 페이지에서 약관을 새창으로 띄울 때 사용되는 config입니다.
 */
const NEW_WINDOW_LAYOUT_CONFIG: MobileLayoutConfig = {
  headerType: ExceptionMobileHeaderType.NewWindow,
  shouldShowHeader: true
};